import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { selectRouteDistance, selectRouteTime } from '../../../features/tripcenter/googleRouteMakerSlice';
import { selectLatestRoute, selectLoadingLatestRoute } from '../../../features/tripcenter/routeSlice';

// Default values for all rules
const defaultValues = (routeDuration, routeLength) => {
    return {
        drivingStartTime: '00:00',
        drivingEndTime: '23:59',
        speedLimit: 60,
        maxHaltTime: 3,
        routeViolationThreshold: 2,
        minGeneratorHours: 0.5 * routeDuration, // 50% of route duration
        minGeneratorHoursPercentage: 50,
        minGeneratorDistance: 0.5 * routeLength, // 50% of route length
        minGeneratorDistancePercentage: 50,
        minTemperature: 15,
        maxTemperature: 25,
        minFuelEfficiency: 3,
        maxFuelConsumption: 100,
    }
};

const RouteRulesModal = ({ open, onClose, onSubmit, initialRules, routeLength, routeDuration }) => {
    const latestRoute = useSelector(selectLatestRoute);
    const loadingLatestRoute = useSelector(selectLoadingLatestRoute);
    const [finalRules, setFinalRules] = useState({});
    const [error, setError] = useState('');
    useEffect(() => {
        //console.log("RouteRulesModal", routeLength, routeDuration, initialRules);
    }, [routeLength, routeDuration, initialRules]);
    // Determine if we're in edit mode (called from TripSettings)
    const isEditMode = !!initialRules;

    useEffect(() => {
        // Handle initialization based on mode
        if (isEditMode) {
            // Edit mode: Use provided initialRules
            //console.log("Edit mode - Using provided rules:", initialRules);
            setFinalRules(initialRules);
        } else {
            // Create mode: Use latest route rules if available
            if (!loadingLatestRoute && latestRoute?.rules) {
                //console.log("Create mode - Using latest route rules");
                setFinalRules(latestRoute.rules);
            } else {
                // No latest rules, use defaults
                //console.log("Create mode - Using default values");
                const defaults = defaultValues(routeDuration, routeLength);
                setFinalRules(defaults);
            }
        }
    }, [open, initialRules, latestRoute, loadingLatestRoute, routeDuration, routeLength, isEditMode]);

    // Modified defaultValues function to handle missing route data
    const getDefaultValues = (duration, length) => {
        const defaults = {
            drivingStartTime: '00:00',
            drivingEndTime: '23:59',
            speedLimit: 60,
            maxHaltTime: 3,
            routeViolationThreshold: 2,
        };

        // Add duration-based rules only if duration is provided
        if (duration) {
            defaults.minGeneratorHours = 0.5 * duration;
            defaults.minGeneratorHoursPercentage = 50;
        }

        // Add length-based rules only if length is provided
        if (length) {
            defaults.minGeneratorDistance = 0.5 * length;
            defaults.minGeneratorDistancePercentage = 50;
            defaults.minFuelEfficiency = 3;
            defaults.maxFuelConsumption = Math.round(length / 3);
        }

        return defaults;
    };

    // Modified handleCheckboxChange
    const handleCheckboxChange = (rule) => {
        //console.log("Checkbox changed", rule);
        setError('');

        // Get appropriate default values based on mode
        const defaultsToUse = isEditMode
            ? getDefaultValues(routeDuration, routeLength)
            : defaultValues(routeDuration, routeLength);

        if (rule === 'drivingStartTime' || rule === 'drivingEndTime') {
            // If either time exists, remove both. If neither exists, add both
            const shouldAdd = !(finalRules.drivingStartTime || finalRules.drivingEndTime);
            setFinalRules(prev => {
                const newRules = { ...prev };
                if (shouldAdd) {
                    newRules.drivingStartTime = defaultsToUse.drivingStartTime;
                    newRules.drivingEndTime = defaultsToUse.drivingEndTime;
                } else {
                    delete newRules.drivingStartTime;
                    delete newRules.drivingEndTime;
                }
                return newRules;
            });
        } else if (rule === 'minGeneratorHours') {
            // Toggle both generator hours and percentage together
            setFinalRules(prev => {
                const newRules = { ...prev };
                if (rule in newRules) {
                    delete newRules.minGeneratorHours;
                    delete newRules.minGeneratorHoursPercentage;
                } else {
                    newRules.minGeneratorHours = defaultsToUse.minGeneratorHours;
                    newRules.minGeneratorHoursPercentage = defaultsToUse.minGeneratorHoursPercentage;
                }
                return newRules;
            });
        } else if (rule === 'minGeneratorDistance') {
            // Toggle both generator distance and percentage together
            setFinalRules(prev => {
                const newRules = { ...prev };
                if (rule in newRules) {
                    delete newRules.minGeneratorDistance;
                    delete newRules.minGeneratorDistancePercentage;
                } else {
                    newRules.minGeneratorDistance = defaultsToUse.minGeneratorDistance;
                    newRules.minGeneratorDistancePercentage = defaultsToUse.minGeneratorDistancePercentage;
                }
                return newRules;
            });
        } else if (rule === 'minTemperature' || rule === 'maxTemperature') {
            // Toggle both temperature fields together
            setFinalRules(prev => {
                const newRules = { ...prev };
                if ('minTemperature' in newRules) {
                    delete newRules.minTemperature;
                    delete newRules.maxTemperature;
                } else {
                    newRules.minTemperature = defaultsToUse.minTemperature;
                    newRules.maxTemperature = defaultsToUse.maxTemperature;
                }
                return newRules;
            });
        } else if (rule === 'minFuelEfficiency') {
            setFinalRules(prev => {
                const newRules = { ...prev };
                if (rule in newRules) {
                    delete newRules.minFuelEfficiency;
                    delete newRules.maxFuelConsumption;
                } else {
                    newRules.minFuelEfficiency = defaultsToUse.minFuelEfficiency;
                    newRules.maxFuelConsumption = routeLength
                        ? Math.round(routeLength / defaultsToUse.minFuelEfficiency)
                        : defaultsToUse.maxFuelConsumption;
                }
                return newRules;
            });
            return;
        } else {
            // Modified default rule handling
            setFinalRules(prev => {
                const newRules = { ...prev };
                if (rule in newRules) {
                    delete newRules[rule];
                } else {
                    newRules[rule] = defaultsToUse[rule];
                }
                return newRules;
            });
        }
    };

    // Modified handleChange
    const handleChange = (rule, value) => {
        setError('');

        // Skip validation for route-dependent rules if data isn't available
        if (!routeLength && ['minGeneratorDistance', 'minFuelEfficiency'].includes(rule)) {
            console.warn(`Cannot update ${rule} without route length`);
            return;
        }
        if (!routeDuration && ['minGeneratorHours'].includes(rule)) {
            console.warn(`Cannot update ${rule} without route duration`);
            return;
        }

        //console.log("change rule", rule);
        //console.log("change value", value);

        // If value is empty or 0, remove the rule entirely
        if (value === '' || value === 0) {
            handleCheckboxChange(rule);
            return;
        }

        if (rule === 'drivingStartTime' || rule === 'drivingEndTime') {
            const otherTimeField = rule === 'drivingStartTime' ? 'drivingEndTime' : 'drivingStartTime';
            const isValid = validateTimeRange(
                rule === 'drivingStartTime' ? value : finalRules.drivingStartTime,
                rule === 'drivingEndTime' ? value : finalRules.drivingEndTime
            );

            if (!isValid) {
                setError('Start time must be before end time');
                return;
            }
        }

        // Add temperature validation
        if (rule === 'minTemperature' || rule === 'maxTemperature') {
            const newMin = rule === 'minTemperature' ? value : finalRules.minTemperature;
            const newMax = rule === 'maxTemperature' ? value : finalRules.maxTemperature;
            const tempError = validateTemperatureRange(newMin, newMax);
            if (tempError) {
                setError(tempError);
                return;
            }
        }

        // Handle generator hours/percentage conversion
        if (rule === 'minGeneratorHours' || rule === 'minGeneratorHoursPercentage') {
            const newRules = { ...finalRules };
            if (rule === 'minGeneratorHours') {
                newRules.minGeneratorHours = value;
                newRules.minGeneratorHoursPercentage = routeDuration ? Math.round(value / routeDuration * 100) : 0;
            } else {
                newRules.minGeneratorHoursPercentage = value;
                newRules.minGeneratorHours = Math.round(routeDuration * value / 100);
            }
            //console.log("newRules", newRules);
            setFinalRules(newRules);
            return;
        }

        // Handle generator distance/percentage conversion
        if (rule === 'minGeneratorDistance' || rule === 'minGeneratorDistancePercentage') {
            const newRules = { ...finalRules };
            if (rule === 'minGeneratorDistance') {
                newRules.minGeneratorDistance = value;
                newRules.minGeneratorDistancePercentage = routeLength ? Math.round(value / routeLength * 100) : 0;
            } else {
                newRules.minGeneratorDistancePercentage = value;
                newRules.minGeneratorDistance = Math.round(routeLength * value / 100);
            }
            setFinalRules(newRules);
            return;
        }

        // Handle fuel efficiency and consumption conversion
        if (rule === 'minFuelEfficiency' || rule === 'maxFuelConsumption') {
            const newRules = { ...finalRules };
            if (rule === 'minFuelEfficiency') {
                newRules.minFuelEfficiency = value;
                newRules.maxFuelConsumption = routeLength ? Math.round(routeLength / value) : 0;
            } else {
                newRules.maxFuelConsumption = value;
                newRules.minFuelEfficiency = routeLength ? Math.round(routeLength / value * 10) / 10 : 0;
            }
            setFinalRules(newRules);
            return;
        }
        //console.log("setFinalRules", finalRules);
        setFinalRules(prev => ({
            ...prev,
            [rule]: value
        }));
    };

    // Add validateTimeRange function
    const validateTimeRange = (startTime, endTime) => {
        if (!startTime || !endTime) return true;

        // Convert time strings to comparable values
        const start = startTime.split(':').map(Number);
        const end = endTime.split(':').map(Number);

        // Compare hours first, then minutes if hours are equal
        if (start[0] < end[0]) return true;
        if (start[0] > end[0]) return false;
        return start[1] <= end[1];
    };

    // Add temperature validation function
    const validateTemperatureRange = (min, max) => {
        if (min === max) {
            return 'Minimum and maximum temperatures cannot be the same';
        }
        if (min > max) {
            return 'Minimum temperature must be lower than maximum temperature';
        }
        if (min === 0 && max === 0) {
            return 'Temperature range cannot be 0-0';
        }
        return '';
    };

    const handleSubmit = () => {
        // Validation logic
        if (error) return;

        // Additional validation for route-dependent rules
        if (finalRules.minGeneratorDistance && !routeLength) {
            setError('Route length is required for generator distance rules');
            return;
        }
        if (finalRules.minGeneratorHours && !routeDuration) {
            setError('Route duration is required for generator hours rules');
            return;
        }

        onSubmit(finalRules);
        onClose();
    };

    if (loadingLatestRoute) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2 }}>Loading Rules data...</Typography>
            </Box>
        );
    } else {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {isEditMode ? 'Edit Route Rules' : 'Set Route Rules'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ width: '100%' }}>
                        {/* Time Controls Group */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '100px 200px 1fr',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Checkbox
                                        checked={!!finalRules.drivingStartTime}
                                        onChange={() => handleCheckboxChange('drivingStartTime')}
                                    />
                                    <Typography>Driving Hours</Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <TextField
                                            type="time"
                                            label="Start Time"
                                            value={finalRules.drivingStartTime || defaultValues.drivingStartTime}
                                            onChange={(e) => handleChange('drivingStartTime', e.target.value)}
                                            disabled={!finalRules.drivingStartTime}
                                            size="small"
                                        />
                                        <Typography>to</Typography>
                                        <TextField
                                            type="time"
                                            label="End Time"
                                            value={finalRules.drivingEndTime || defaultValues.drivingEndTime}
                                            onChange={(e) => handleChange('drivingEndTime', e.target.value)}
                                            disabled={!finalRules.drivingEndTime}
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Other Controls */}
                        <Box sx={{
                            display: 'grid',
                            gap: 2,
                            '& .rule-row': {
                                display: 'grid',
                                gridTemplateColumns: '100px 200px 1fr',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                            }
                        }}>
                            {/* Speed Limit */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={!!finalRules.speedLimit}
                                    onChange={() => handleCheckboxChange('speedLimit')}
                                />
                                <Typography>Speed Limit</Typography>
                                <TextField
                                    type="number"
                                    value={finalRules.speedLimit || defaultValues.speedLimit}
                                    onChange={(e) => handleChange('speedLimit', Number(e.target.value))}
                                    disabled={!finalRules.speedLimit}
                                    size="small"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">km/h</InputAdornment>,
                                    }}
                                />
                            </Box>
                            {/* Fuel Rules */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={'minFuelEfficiency' in finalRules}
                                    onChange={() => handleCheckboxChange('minFuelEfficiency')}
                                />
                                <Typography>Fuel Rules</Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                        type="number"
                                        label="Min Efficiency"
                                        value={finalRules.minFuelEfficiency || defaultValues.minFuelEfficiency}
                                        onChange={(e) => handleChange('minFuelEfficiency', Number(e.target.value))}
                                        disabled={!('minFuelEfficiency' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">km/L</InputAdornment>,
                                        }}
                                    />
                                    <Typography>or</Typography>
                                    <TextField
                                        type="number"
                                        label="Max Consumption"
                                        value={finalRules.maxFuelConsumption || defaultValues.maxFuelConsumption}
                                        onChange={(e) => handleChange('maxFuelConsumption', Number(e.target.value))}
                                        disabled={!('minFuelEfficiency' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">L</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Box>
                            {/* maxHaltTime */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={!!finalRules.maxHaltTime}
                                    onChange={() => handleCheckboxChange('maxHaltTime')}
                                />
                                <Typography>Maximum Halt Time</Typography>
                                <TextField
                                    type="number"
                                    value={finalRules.maxHaltTime || defaultValues.maxHaltTime}
                                    onChange={(e) => handleChange('maxHaltTime', Number(e.target.value))}
                                    disabled={!finalRules.maxHaltTime}
                                    size="small"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">Hours</InputAdornment>,
                                    }}
                                />
                            </Box>

                            {/* routeViolationThreshold */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={!!finalRules.routeViolationThreshold}
                                    onChange={() => handleCheckboxChange('routeViolationThreshold')}
                                />
                                <Typography>Route Violation Threshold</Typography>
                                <TextField
                                    type="number"
                                    value={finalRules.routeViolationThreshold || defaultValues.routeViolationThreshold}
                                    onChange={(e) => handleChange('routeViolationThreshold', Number(e.target.value))}
                                    disabled={!finalRules.routeViolationThreshold}
                                    size="small"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">km</InputAdornment>,
                                    }}
                                />
                            </Box>

                            {/* Generator Hours */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={'minGeneratorHours' in finalRules}
                                    onChange={() => handleCheckboxChange('minGeneratorHours')}
                                />
                                <Typography>Generator Hours</Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                        type="number"
                                        value={finalRules.minGeneratorHours || defaultValues.minGeneratorHours}
                                        onChange={(e) => handleChange('minGeneratorHours', Number(e.target.value))}
                                        disabled={!('minGeneratorHours' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                                        }}
                                    />
                                    <Typography>or</Typography>
                                    <TextField
                                        type="number"
                                        value={finalRules.minGeneratorHoursPercentage || defaultValues.minGeneratorHoursPercentage}
                                        onChange={(e) => handleChange('minGeneratorHoursPercentage', Number(e.target.value))}
                                        disabled={!('minGeneratorHours' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Generator Distance */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={'minGeneratorDistance' in finalRules}
                                    onChange={() => handleCheckboxChange('minGeneratorDistance')}
                                />
                                <Typography>Generator Distance</Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                        type="number"
                                        value={finalRules.minGeneratorDistance || defaultValues.minGeneratorDistance}
                                        onChange={(e) => handleChange('minGeneratorDistance', Number(e.target.value))}
                                        disabled={!('minGeneratorDistance' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">km</InputAdornment>,
                                        }}
                                    />
                                    <Typography>or</Typography>
                                    <TextField
                                        type="number"
                                        value={finalRules.minGeneratorDistancePercentage || defaultValues.minGeneratorDistancePercentage}
                                        onChange={(e) => handleChange('minGeneratorDistancePercentage', Number(e.target.value))}
                                        disabled={!('minGeneratorDistance' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Temperature Range */}
                            <Box className="rule-row">
                                <Checkbox
                                    checked={'minTemperature' in finalRules}
                                    onChange={() => handleCheckboxChange('minTemperature')}
                                />
                                <Typography>Temperature Range</Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                        type="number"
                                        value={finalRules.minTemperature || defaultValues.minTemperature}
                                        onChange={(e) => handleChange('minTemperature', Number(e.target.value))}
                                        disabled={!('minTemperature' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                                        }}
                                    />
                                    <Typography>to</Typography>
                                    <TextField
                                        type="number"
                                        value={finalRules.maxTemperature || defaultValues.maxTemperature}
                                        onChange={(e) => handleChange('maxTemperature', Number(e.target.value))}
                                        disabled={!('minTemperature' in finalRules)}
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Box>


                        </Box>
                    </Box>
                </DialogContent>

                {error && (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {isEditMode ? 'Save Changes' : 'Set Rules'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
};

export default RouteRulesModal; 